import { FC, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { merge } from "lodash";
// Context
import { useStream } from "./StreamContext";
// Redux
import { useAppSelector } from "hooks/useStore";
import { selectCurrentUser } from "store/currentUser/currentUserSelectors";
import { selectConsents } from "store/consents/consentsSelectors";
import QuizTypes from "types/QuizTypes";
// i18next
import { useTranslation } from "react-i18next";
// Icons
import { Trophy, Target, BarChart, ClipboardList, User, Users, Swords, Flame } from 'lucide-react';
// Steps
import AccordionStep from "./CreateSteps/AccordionStep";
import SelectedQuizTypeBadge from "./CreateSteps/SelectedQuizTypeBadge";
import QuizTypeStep from "./CreateSteps/QuizTypeStep";
import QuizDetailsStep from "./CreateSteps/QuizDetailsStep";
import QuizBrandingStep from "./CreateSteps/QuizBrandingStep";
// Utilities
import { formValuesToQuiz } from "utilities/quizFormMapping";
import { buildQuizTypeDefaults, scoringQuizTypes } from "utilities/quizTypeDefaults";
import { teamSelectorFormations } from "utilities/utilities";

type Props = {
  setStep: (step:number) => void;
}

// All quiz types share the same agent instructions, differing only in the
// noun used for the game/type being created and whether the type has a
// "correct answer" that question images must avoid giving away. Edit the
// shared checklist here once instead of copy-pasting it into every type's
// prompt below.
type QuizTypePromptConfig = {
  gameNoun: string;
  // True for types with a right/wrong answer (Scoring, Prediction, Beat The
  // Expert, Streak), where a question image must not spoil the answer. False
  // for types with no correct answer (Poll, Ranking Poll, Survey,
  // Personality, Team Selector), where the image can carry answer context.
  hideAnswersInImages: boolean;
};

const buildQuizTypePrompt = ({ gameNoun, hideAnswersInImages }: QuizTypePromptConfig): string => [
  `Collect the info you need to create a ${gameNoun}, starting by asking about the game topic or theme.`,
  `Strictly follow these instructions and treat this as a strict checklist:`,
  '',
  `- Ask one question at a time until all answers are provided.`,
  `- Do not number the questions — ask them naturally, one by one.`,
  `- For each question, suggest relevant options based on the topic selected (e.g. game style, UI style, target audience, mechanics, mood/tone). Each question should be distinct.`,
  `- Question images must be generated for each question${hideAnswersInImages ? ', question images should NOT contain answers or tips' : ''}.`,
  `- Add a CTA page at the end.`,
  `- Do not generate separate landing page titles, button title, or its circle image (landingPage.image). Instead, bake the quiz title directly into the landing page background image (landingPage.backgroundImage) as stylized typography/artwork (like a movie poster or album cover), so the background image alone conveys the title with no text or circle image overlaid on top of it. Place the title around the vertical middle of the image, clear of both edges: the top has a "Game ends in" countdown bar overlaid on it, and the bottom has the "Play now" button (and sometimes a "View prize" button and language selector) overlaid on it — keep both of those areas free of title text.`,
  `- Generate a wallpaper image: a plain image with no pictures or subjects — just a solid or softly gradient color that complements the quiz's color scheme.`,
  `- Based on the colors used in the background images, apply matching font and theme colors throughout. Keep the center visually clean, especially the upper-middle region (30–50% of the image height), to provide a clear safe area for overlaid quiz text and buttons.`,
  `- Start asking the first question immediately with no response to this prompt.`,
  `- Before giving your final answer, compare your output against each instruction line by line.`,
  `- Make final statement shown after game generated concise. It should include suggestion to manually create interstitial, timer or survey pages along with question pages (there is a plus icon button), enabled waiver page if needed, customize each page individually or manage styles (there is a “Edit style” button at the top), and configure social share for the Leaderboard page (edit that page to enable it and set the hashtags/URL/image).`,
].join('\n');

const QUIZ_TYPE_PROMPT_CONFIG: Record<QuizTypes, QuizTypePromptConfig> = {
  [QuizTypes.Scoring]: { gameNoun: 'game', hideAnswersInImages: true },
  [QuizTypes.Prediction]: { gameNoun: 'game', hideAnswersInImages: true },
  [QuizTypes.Poll]: { gameNoun: 'poll game', hideAnswersInImages: false },
  [QuizTypes.RankingPoll]: { gameNoun: 'ranking poll game', hideAnswersInImages: false },
  [QuizTypes.Survey]: { gameNoun: 'survey game', hideAnswersInImages: false },
  [QuizTypes.Personality]: { gameNoun: 'personality quiz game', hideAnswersInImages: false },
  [QuizTypes.TeamSelector]: { gameNoun: 'team selector game', hideAnswersInImages: false },
  [QuizTypes.BeatTheExpert]: { gameNoun: 'beat the expert game', hideAnswersInImages: true },
  [QuizTypes.Streak]: { gameNoun: 'streak game', hideAnswersInImages: true },
};

const QuizAgentCreateScreen:FC<Props> = ({ setStep }) => {
  const { t } = useTranslation();

  const { startStream, startThread } = useStream();

  const { getValues, setValue, control, watch } = useFormContext<any>();

  const logoFormatWatcher = watch('logoFormat');
  const sponsorImageFormatWatcher = watch('sponsorImageFormat');
  const logoImageWatcher = watch('logoImage');
  const sponsorImageWatcher = watch('sponsorImage');
  const promoVideoWatcher = watch('promoVideo');
  const uploadedBrandingCount = [logoImageWatcher, sponsorImageWatcher, promoVideoWatcher].filter(Boolean).length;

  const currentUser = useAppSelector(selectCurrentUser);
  const consents = useAppSelector(selectConsents);

  const availableQuizTypes = useMemo(() => currentUser?.account?.quizTypes || [], [currentUser]);

  // Which of the three create-flow steps (type -> details -> branding) is
  // currently expanded. Advances via each step's onNext/onBack, or jumps
  // directly when the user clicks a collapsed step's header.
  const [activeStep, setActiveStep] = useState<number>(1);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState<boolean>(true);
  const [isDebugMode, setIsDebugMode] = useState<boolean>(false);
  const [difficulty, setDifficulty] = useState<string>('Easy');
  const [questionCount, setQuestionCount] = useState<number | string>(2);
  const [questionTimeout, setQuestionTimeout] = useState<number | string>(10);
  const [questionScore, setQuestionScore] = useState<number | string>(1000);
  const [teamSelectorFormation, setTeamSelectorFormation] = useState<string>('4-4-2');
  const [selectedType, setSelectedType] = useState<QuizTypes>(QuizTypes.Scoring);
  const [topic, setTopic] = useState<string>('');
  const [editablePrompt, setEditablePrompt] = useState<string>('');

  // selectedType starts on Scoring as a reasonable guess before the account
  // loads. Once availableQuizTypes arrives, correct it the same way
  // QuizFormDialog's defaultType does — but only if the current selection
  // isn't actually one of the account's types, so a manual pick already
  // made from the (already-filtered) grid is never clobbered.
  useEffect(() => {
    if (!availableQuizTypes.length) return;
    if (availableQuizTypes.includes(selectedType)) return;
    setSelectedType(availableQuizTypes.includes(QuizTypes.Scoring) ? QuizTypes.Scoring : availableQuizTypes[0]);
  }, [availableQuizTypes, selectedType]);

  const isScoringType = scoringQuizTypes.includes(selectedType);
  const isTeamSelectorType = selectedType === QuizTypes.TeamSelector;

  const changeQuestionCount = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (!/^\d*$/.test(value)) return;
    setQuestionCount(value ? Number(value) : value);
  }

  const changeQuestionTimeout = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (!/^\d*$/.test(value)) return;
    setQuestionTimeout(value ? Number(value) : value);
  }

  const changeQuestionScore = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (!/^\d*$/.test(value)) return;
    setQuestionScore(value ? Number(value) : value);
  }

  const handleProceed = async () => {
    if (!topic.trim() || !selectedType) return;
    // Guard: wait for consents to load so required consent IDs are not lost.
    if (consents === null) return;

    // Sync the chosen quiz type into the unified form so subsequent
    // formValuesToQuiz() snapshots carry it as the draftQuiz.
    setValue('type', selectedType);

    // The form was seeded with Scoring-shaped defaults regardless of the type
    // picked here — apply the same per-type copy/defaults QuizFormDialog uses
    // (landing page copy, registration caption, leaderboard/CTA copy, results
    // page styling) so the agent starts from a quiz that already looks right
    // for the chosen type.
    const typeDefaults = buildQuizTypeDefaults(selectedType);
    const currentValues = getValues();
    // There's no date picker in this step (unlike QuizFormDialog), so seed
    // startsDate with "now" instead of leaving it blank.
    const startsDate = currentValues.startsDate || new Date();
    // The base form (and buildQuizTypeDefaults) seed the landing page with
    // placeholder copy ("Sample Text 1") and a "YOUR LOGO HERE" circle image
    // — fine for the manual QuizFormDialog flow, but the agent bakes the
    // title into the generated background image instead, so clear these to
    // avoid the placeholders leaking into the created quiz.
    const landingPageOverrides = { landingPage: { title1: '', title2: '', title3: '', image: '' } };
    const mergedValues = merge({}, currentValues, typeDefaults, landingPageOverrides, { type: selectedType, startsDate });
    Object.keys(typeDefaults).forEach((key) => setValue(key as any, mergedValues[key]));
    setValue('startsDate', startsDate);

    const draftQuiz = formValuesToQuiz(mergedValues);

    const requiredConsents = consents.filter(consent => consent.language === 'en' && consent.required).map(consent => consent._id);
    draftQuiz.consents = requiredConsents;

    const result = await startThread({ quizType: selectedType, draftQuiz });
    // Aborted (e.g. dialog closed mid-request) or failed — nothing to
    // continue with, and the component may already be unmounted.
    if (!result.payload?.threadId) return;

    // Fall back to the config prompt when editablePrompt is still empty (e.g.
    // availableQuizTypes arrived after mount so the sync effect ran too early).
    const activePrompt = editablePrompt || quizTypes.find(type => type.id === selectedType)?.prompt || '';
    const scoringDetails = isScoringType
      ? ` Difficulty: ${difficulty}. Question timeout: ${questionTimeout} seconds. Question score: ${questionScore} points.`
      : '';
    // Team Selector has a fixed one-question-per-position structure driven by
    // the chosen formation, so the free-typed questionCount doesn't apply —
    // the position list itself dictates how many questions to create and
    // what each one is about.
    const teamSelectorPositions = isTeamSelectorType ? teamSelectorFormations[teamSelectorFormation] : null;
    const effectiveQuestionCount = teamSelectorPositions ? teamSelectorPositions.length : questionCount;
    const teamSelectorDetails = teamSelectorPositions
      ? ` Formation: ${teamSelectorFormation}. Create exactly one question per position, in this order: ${teamSelectorPositions.map(position => position.label).join(', ')}. Phrase each question as "Pick your [position]" and offer realistic player name options related to the topic — no correct answer.`
      : '';
    // Only logoImage needs this: it's the one branding asset the agent can
    // actually generate on its own, so it's the only one at risk of being
    // silently overwritten if the user already uploaded one in Branding.
    const logoInstruction = draftQuiz.logoImage
      ? ' - Logo image is already provided, do not generate or override it.'
      : '';
    const message = `Create a ${selectedType} quiz. Topic: ${topic}. Number of Questions: ${effectiveQuestionCount}.${scoringDetails}${teamSelectorDetails}${logoInstruction} ${activePrompt}`

    const data = {
      threadId: result.payload.threadId,
      message,
      draftQuiz,
    };

    startStream(data);

    setTopic('');
    setStep(2);
  };

  const quizTypes = useMemo(() => [
    {
      id: QuizTypes.Scoring,
      title: 'Scoring Quiz',
      description: 'Users complete the quiz and receive a final score. Ideal for knowledge tests and competitions.',
      icon: Trophy,
      status: 'active'
    },
    {
      id: QuizTypes.Prediction,
      title: 'Prediction Quiz',
      description: 'Users guess future outcomes. Points awarded based on actual results.',
      icon: Target,
      status: 'active'
    },
    {
      id: QuizTypes.Poll,
      title: 'Poll',
      description: 'Gather opinions with no right or wrong answers. Show consensus.',
      icon: BarChart,
      status: 'active'
    },
    {
      id: QuizTypes.RankingPoll,
      title: 'Ranking Poll',
      description: 'All questions share one answer pool. Each picked answer is removed, so no option can be chosen twice.',
      icon: ClipboardList,
      status: 'active'
    },
    {
      id: QuizTypes.Survey,
      title: 'Survey',
      description: 'Collect detailed feedback with mixed question types.',
      icon: ClipboardList,
      status: 'active'
    },
    {
      id: QuizTypes.Personality,
      title: 'Personality',
      description: 'Users answer questions to reveal their personality type or profile.',
      icon: User,
      status: 'active'
    },
    {
      id: QuizTypes.TeamSelector,
      title: 'Team Selector',
      description: 'Users pick a player for each position to build their dream lineup.',
      icon: Users,
      status: 'active'
    },
    {
      id: QuizTypes.BeatTheExpert,
      title: 'Beat The Expert',
      description: 'Users try to outscore an expert\'s predicted answers to win.',
      icon: Swords,
      status: 'active'
    },
    {
      id: QuizTypes.Streak,
      title: 'Streak',
      description: 'Users keep answering correctly to build the longest streak they can.',
      icon: Flame,
      status: 'active'
    }
  ]
    .filter(type => availableQuizTypes.includes(type.id))
    .map(type => ({ ...type, prompt: buildQuizTypePrompt(QUIZ_TYPE_PROMPT_CONFIG[type.id]) })),
    [availableQuizTypes]);

  // Keep editable prompt in sync when quiz type changes or when
  // availableQuizTypes arrives async (which rebuilds the quizTypes list).
  useEffect(() => {
    const found = quizTypes.find(type => type.id === selectedType);
    setEditablePrompt(found?.prompt ?? '');
  }, [selectedType, availableQuizTypes, quizTypes]);

  const selectedQuizType = quizTypes.find(type => type.id === selectedType);
  const defaultPrompt = selectedQuizType?.prompt ?? '';

  return (
    <div className="w-full xl:w-9/12 mx-auto space-y-4">
      <AccordionStep
        stepNumber={1}
        title={t('dialogs.quizAgent.selectGameType')}
        isActive={activeStep === 1}
        isCompleted={activeStep !== 1 && Boolean(selectedType)}
        summary={selectedQuizType && <SelectedQuizTypeBadge title={selectedQuizType.title} icon={selectedQuizType.icon} />}
        onHeaderClick={() => setActiveStep(1)}
      >
        <QuizTypeStep
          quizTypes={quizTypes}
          selectedType={selectedType}
          setSelectedType={setSelectedType}
          onNext={() => setActiveStep(2)}
        />
      </AccordionStep>

      <AccordionStep
        stepNumber={2}
        title={t('dialogs.quizAgent.whatIsTheQuizAbout')}
        isActive={activeStep === 2}
        isCompleted={activeStep !== 2 && Boolean(topic.trim())}
        summary={topic.trim() && <span className="text-sm text-gray-400 truncate block max-w-full">{topic}</span>}
        onHeaderClick={() => setActiveStep(2)}
      >
        <QuizDetailsStep
          topic={topic}
          setTopic={setTopic}
          isAdvancedOpen={isAdvancedOpen}
          setIsAdvancedOpen={setIsAdvancedOpen}
          isDebugMode={isDebugMode}
          setIsDebugMode={setIsDebugMode}
          isScoringType={isScoringType}
          difficulty={difficulty}
          setDifficulty={setDifficulty}
          questionCount={questionCount}
          changeQuestionCount={changeQuestionCount}
          questionTimeout={questionTimeout}
          changeQuestionTimeout={changeQuestionTimeout}
          questionScore={questionScore}
          changeQuestionScore={changeQuestionScore}
          isTeamSelectorType={isTeamSelectorType}
          teamSelectorFormation={teamSelectorFormation}
          setTeamSelectorFormation={setTeamSelectorFormation}
          editablePrompt={editablePrompt}
          setEditablePrompt={setEditablePrompt}
          defaultPrompt={defaultPrompt}
          onNext={() => setActiveStep(3)}
          onBack={() => setActiveStep(1)}
        />
      </AccordionStep>

      <AccordionStep
        stepNumber={3}
        title={t('dialogs.quizAgent.brandingTitle')}
        tag={t('dialogs.quizAgent.optionalLabel')}
        isActive={activeStep === 3}
        isCompleted={false}
        summary={<span className="text-sm text-gray-400">{uploadedBrandingCount}/3</span>}
        onHeaderClick={() => setActiveStep(3)}
      >
        <QuizBrandingStep
          control={control}
          logoFormatWatcher={logoFormatWatcher}
          sponsorImageFormatWatcher={sponsorImageFormatWatcher}
          disabled={!topic.trim() || !selectedType || consents === null}
          onBack={() => setActiveStep(2)}
          onSubmit={handleProceed}
        />
      </AccordionStep>
    </div>
  );
};

export default QuizAgentCreateScreen;
